import { createAsyncThunk } from "@reduxjs/toolkit"

import type { IPagination } from "@common-shared/types/general.types"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { NETWORK_STATUS_CONFIG } from "../../config/networkStatus.config"
import type { RawNetworkStatusRuleCsvRowDTO } from "../../dto/rawNetworkStatusRuleCsvInput.dto"
import { CsvHelper } from "../../helpers/csv.helper"
import { NetworkStatusRulesCsvHelper } from "../../helpers/networkStatusRulesCsv.helper"
import { NetworkStatusService } from "../../services/networkStatus.service"
import type {
    IThunkActionWithPracticeData,
    IThunkActionWithPracticeOrIdData,
} from "../../types/general.types.ts"
import type { INetworkStatusRulesFilters } from "../../types/networkStatus.types"
import {
    networkStatusSetExportProgress,
    networkStatusSetFilters,
    networkStatusSetList,
    networkStatusSetVersions,
} from "../slices/networkStatus.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"
import { userDetailsGetProfile } from "./userDetails.thunks"

/**
 * Get list of network status rules
 */
export const networkStatusGetRulesList = createAsyncThunk<
    void,
    IThunkActionWithPracticeOrIdData<{
        pagination: IPagination
        version: number
        filters?: INetworkStatusRulesFilters
    }>
>("networkStatusGetRulesListPrefix", async (action, { dispatch, signal }) => {
    dispatch(runtimeStartLoading("networkStatusGetRulesListLoading"))

    try {
        const result = await NetworkStatusService.getRulesList(
            action.practice,
            action.payload.version,
            action.payload.pagination,
            action.payload.filters,
            signal,
        )

        if (result) {
            dispatch(networkStatusSetList(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("networkStatusGetRulesListLoading"))

        action.onFinally?.()
    }
})

/**
 * Get list of network status rules versions for the practice.
 */
export const networkStatusGetVersions = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<undefined>
>("networkStatusGetVersionsPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("networkStatusGetVersionsLoading"))

    dispatch(networkStatusSetVersions([]))

    try {
        const result = await NetworkStatusService.getVersions(action.practice)

        if (!result) {
            throw new Error()
        }

        dispatch(networkStatusSetVersions(result))

        action.onSuccess?.()
    } catch {
        dispatch(networkStatusSetVersions([]))

        action.onError?.()
    } finally {
        dispatch(runtimeStopLoading("networkStatusGetVersionsLoading"))

        action.onFinally?.()
    }
})

/**
 * Set the practice's active network status version. On success, refreshes profile
 * so selected practice role has updated networkStatusRulesVersion.
 */
export const networkStatusSetActiveVersion = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{ versionId: number }>
>("networkStatusSetActiveVersionPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("networkStatusSetActiveVersionLoading"))

    try {
        const result = await NetworkStatusService.setActiveVersion(
            action.practice,
            action.payload.versionId,
        )

        if (!result?.success) {
            throw new Error()
        }

        ToastrHelper.success("Active network status rules version updated.")

        await dispatch(
            userDetailsGetProfile({
                payload: undefined,
                onSuccess: action.onSuccess,
            }),
        )
    } catch {
        ToastrHelper.error("Failed to set active network status rules version.")

        action.onError?.()
    } finally {
        dispatch(runtimeStopLoading("networkStatusSetActiveVersionLoading"))

        action.onFinally?.()
    }
})

/**
 * Export rules as CSV for current filters and version.
 * Fetches full list in pages, builds upload-compatible CSV, triggers download.
 */
export const networkStatusExportRulesList = createAsyncThunk<
    void,
    IThunkActionWithPracticeOrIdData<{
        version: number
        filters?: INetworkStatusRulesFilters
    }>
>("networkStatusExportRulesListPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("networkStatusExportRulesListLoading"))
    dispatch(networkStatusSetExportProgress(0))

    try {
        await CsvHelper.fetchPagesAndDownloadCsv({
            pageSize: NETWORK_STATUS_CONFIG.csvExportPageSize,
            fetchPage: pagination =>
                NetworkStatusService.getRulesList(
                    action.practice,
                    action.payload.version,
                    pagination,
                    action.payload.filters,
                ),
            headers: Object.keys(NETWORK_STATUS_CONFIG.csvExportHeadersToKey),
            mapRow: NetworkStatusRulesCsvHelper.ruleToCsvRow,
            fileName: NETWORK_STATUS_CONFIG.csvExportBaseFileName,
            onProgress: progress => {
                dispatch(networkStatusSetExportProgress(progress))
            },
        })

        action.onSuccess?.()
    } catch (e) {
        action.onError?.()
    } finally {
        dispatch(runtimeStopLoading("networkStatusExportRulesListLoading"))
        dispatch(networkStatusSetExportProgress(0))
        action.onFinally?.()
    }
})

/**
 * Validate network status rules (e.g. before bulk upload). Result { errors } passed to onSuccess.
 */
export const networkStatusValidateRules = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        rules: RawNetworkStatusRuleCsvRowDTO[]
    }>
>("networkStatusValidateRulesPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("networkStatusValidateRulesLoading"))

    try {
        const result = await NetworkStatusService.validateRules(
            action.practice,
            { rules: action.payload.rules },
        )

        action.onSuccess?.(result ?? { errors: [] })
    } catch {
        ToastrHelper.error("Failed to validate network status rules.")

        action.onError?.()
    } finally {
        dispatch(runtimeStopLoading("networkStatusValidateRulesLoading"))
    }
})

/**
 * Create network status rules from review (chunked). On success: clears filters, refreshes profile, then calls onSuccess.
 */
export const networkStatusCreateRulesFromReview = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        rules: RawNetworkStatusRuleCsvRowDTO[]
    }>
>("networkStatusCreateRulesFromReviewPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("networkStatusCreateRulesFromReviewLoading"))

    try {
        const validRules = action.payload.rules.map((r, i) => ({
            ...r,
            index: i,
        }))

        let versionId: number | undefined = undefined

        for (
            let start = 0;
            start < validRules.length;
            start += NETWORK_STATUS_CONFIG.createChunkSize
        ) {
            const chunk = validRules
                .slice(start, start + NETWORK_STATUS_CONFIG.createChunkSize)
                .map((r, i) => {
                    // Keep original index if it exists (for validation error mapping), otherwise fall back to global position
                    return { ...r, index: r.index ?? start + i }
                })

            const result = await NetworkStatusService.createRules(
                action.practice,
                { versionId, rules: chunk },
            )

            if (result.errors?.length) {
                ToastrHelper.error(
                    result.errors[0]?.error ?? "Failed to add rules",
                )

                action.onError?.()

                return
            }

            if (typeof versionId === "undefined") {
                versionId = result.versionId ?? result.success?.[0]?.version?.id
            }
        }

        ToastrHelper.success("Network status rules imported successfully.")

        dispatch(networkStatusSetFilters({}))

        await dispatch(
            userDetailsGetProfile({
                payload: undefined,
                onSuccess: action.onSuccess,
            }),
        )
    } catch {
        action.onError?.()
    } finally {
        dispatch(
            runtimeStopLoading("networkStatusCreateRulesFromReviewLoading"),
        )

        action.onFinally?.()
    }
})
